import React, { useCallback, useEffect, useState } from "react";

import { Box, Grid, useScreenSize } from "@telefonica/mistica";
import { observer, useLocalObservable } from "mobx-react-lite";
import { createSearchParams, redirect } from "react-router-dom";
import { AnticipationAdvantages, ContractConditions, InformationAboutFGTS, PreSimulationForm } from "../../components";
import { FAQ, Footer, HeroSection, HowToAuthorizeSlide, NavBar } from "../../components";
import { parseMoneyToDecimal } from "../../utils/formatters";
import type { HelmetProps } from "react-helmet";
import { Helmet as HelmetImport } from "react-helmet";
import { Store } from "./store";
import { strings } from "../../localization";
import { useAnalytics } from "../../hooks/useAnalytics";
import BrandChangeNotification from "../../components/BrandChangeNotification";
import { colors } from "../../theme/colors";
import { useGlobalStore } from "../../contexts/globalStoreContext";
import { getFeatureFlagIsVisibleColdSimulationFGTS } from "../../services/SystemConfiguration";
import { useNavigateCustom } from "../../hooks";

const metaPixelScript = `
  !function(f,b,e,v,n,t,s)
  {if(f.fbq)return;n=f.fbq=function() {n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)}
  ;if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}
  (window, document,'script',
  'https://connect.facebook.net/en_US/fbevents.js');
  fbq('init', '3233861423550602');
  fbq('track', 'PageView');
`;

const Helmet = HelmetImport as unknown as React.ComponentClass<HelmetProps>;

const Home: React.FC = observer(() => {
	const { title, buttonTitle } = strings.pages.home.howToAuthorizeSlide;
	const { gtmTitle, gtmDescription } = strings.pages.home;
	const { buttonPrimary } = strings.components.InformationAboutFGTS;
	const store = useLocalObservable(() => new Store());
	const navigate = useNavigateCustom();
	const { isDesktopOrBigger } = useScreenSize();
	const { authenticationStore } = useGlobalStore();
	const [isVisibleColdSimulator, setIsVisibleColdSimulator] = useState(false);

	const onSubmitPreSimulationForm = (): void => {
		const balance = parseMoneyToDecimal(store.FGTSBalanceInput.field.value).toString();
		const birthMonth = store.birthMonthInput.field.value;

		navigate({
			pathname: "/simulator-lead",
			search: createSearchParams({
				balance,
				birthMonth,
			}).toString(),
		});
	};

	const onContinue = (): void => {
		navigate("/login");
	};

	const handleOnPress = (): void => {
		addAnalyticEvent({
			component: "body",
			action: "click:button",
			description: buttonTitle,
			step: "home:condicoes:solicitacao",
		}, { applySanitize: true });

		navigate("/login");
	};

	const { addAnalyticEvent, updatePageView } = useAnalytics();

	const handleAnalyticInteraction = (step: string, description?: string): void => {
		addAnalyticEvent({
			component: "body",
			action: "click:button",
			description: description ? description : buttonTitle,
			step,
			value: "",
		}, { applySanitize: true });
	};

	useEffect(() => {
		updatePageView({
			name: "Vivo Pay FGTS",
			pagePath: "/fgts",
		});
	}, []);

	useEffect(() => {
		sessionStorage.clear();
		authenticationStore.logout();
	}, []);

	// Busca valor da feature flag isVisibleColdSimulationFGTS
	const fetchFeatureFlag = useCallback(async (): Promise<void> => {
		const isVisibleColdSimulationFGTS = await getFeatureFlagIsVisibleColdSimulationFGTS();
		setIsVisibleColdSimulator(isVisibleColdSimulationFGTS);
	}, []);

	useEffect(() => {
		fetchFeatureFlag();
	}, []);

	useEffect(() => {
		const newDomain = "fgts.vivopay.com.br";

		// redireciona para o novo domínio /fgts
		if (window.location.origin.includes(newDomain)) {
			redirect("/fgts");
		}
	}, []);

	return (
		<div style={{ background: colors.white[550] }}>
			<Helmet>
				<script>{metaPixelScript}</script>
				<meta name="description" content={gtmDescription} />
				<meta property="og:title" content={gtmTitle} />
				<meta property="og:description" content={gtmDescription} />
				<title>{gtmTitle}</title>
			</Helmet>
			<NavBar />
			<BrandChangeNotification />
			<Box className="center-aligned">
				<HeroSection />
			</Box>
			<Box className="center-aligned font-family-vivotype">
				<Grid columns={1}>
					<Box className="center-aligned">
						<Grid columns={{ maxSize: 1100 }}>
							{isVisibleColdSimulator ? (
								<Box paddingX={24} paddingBottom={24} className={"home-cold-simulator-box"}>
									<PreSimulationForm
										birthMonth={store.birthMonthInput.field.value}
										fgtsBalance={store.FGTSBalanceInput.field.value}
										onSelectBirthMonth={store.birthMonthInput.onChange}
										onChangeFGTSBalance={store.FGTSBalanceInput.onChange}
										onSubmit={onSubmitPreSimulationForm}
									/>
								</Box>
							) : null}
							<Box paddingX={24} paddingY={isDesktopOrBigger ? 80 : 56}>
								<HowToAuthorizeSlide
									title={title}
									buttonTitle={buttonTitle}
									onSubmit={() => {
										onContinue();
										handleAnalyticInteraction("home:sliders:etapas-antecipacao");
									}}
									alignButton="left" />
							</Box>
						</Grid>
					</Box>
					<Grid columns={{ maxSize: 1224 }}>
						<Box paddingY={isDesktopOrBigger ? 24 : 24}>
							<AnticipationAdvantages
								buttonTitle={buttonTitle}
								onContinue={() => {
									onContinue();
									handleAnalyticInteraction("home:sliders:vantagens-fgts");
								}}
							/>
						</Box>
					</Grid>
				</Grid>
			</Box>
			<ContractConditions handleOnPress={handleOnPress} />
			<InformationAboutFGTS
				onContinue={(): void => {
					onContinue();
					handleAnalyticInteraction("home:o-que-e-antecipacao", buttonPrimary);
				}}
			/>
			<FAQ analyticStepName="home:faq" />
			<Footer />
		</div>
	);
});

export default Home;
